import React, { useState, useEffect, useRef } from "react";
import CountdownTimer from "../components/CountdownTimer";
import { Lantern, GoldenHorse, RedEnvelope } from "../components/LunarNewYearDecorations";
import { Sparkles, Cloud } from "lucide-react";
import ProfileDropdown from "../components/ProfileDropdown";
import { Bell, Search, Sun, Moon, Check, MessageSquare, Menu } from "lucide-react";
import Button from "../components/common/Button";
import { useTheme } from "../context/ThemeContext";
import api, { getSocketBaseUrl } from "../api";
import { io } from "socket.io-client";
import { useAuth } from "../hooks/useAuth";
import { formatDistanceToNow } from "date-fns";
import { vi } from "date-fns/locale";

const Header = ({ title, onMenuToggle }) => {
  const { theme, toggleTheme } = useTheme();
  const { currentUser } = useAuth();
  
  // Notification State
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch Notifications
  const fetchNotifications = async () => {
    if (!currentUser) return;
    try {
        const res = await api.get("/notifications");
        setNotifications(res.data);
        setUnreadCount(res.data.filter(n => !n.is_read).length);
    } catch (err) {
        console.error("Failed to fetch notifications");
    }
  };

  useEffect(() => {
    fetchNotifications();

    // Listen for live updates
    const socketUrl = getSocketBaseUrl();
    const socket = io(socketUrl, {
        transports: ["websocket", "polling"]
    });
    
    if (currentUser) {
        socket.emit("register", currentUser.id);
        socket.on("notification", () => {
            // Refetch when new notification arrives
            fetchNotifications(); 
        });
    }

    return () => socket.disconnect();
  }, [currentUser]);

  // Click outside to close
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const markAsRead = async (id) => {
      try {
          await api.put(`/notifications/${id}/read`);
          setNotifications(prev => prev.map(n => n.id === id ? {...n, is_read: 1} : n));
          setUnreadCount(prev => Math.max(0, prev - 1));
      } catch(err) {}
  };

  const markAllRead = async () => {
      try {
          await api.put("/notifications/read-all");
          setNotifications(prev => prev.map(n => ({...n, is_read: 1})));
          setUnreadCount(0);
      } catch(err) {}
  };

  return (
    <header className="sticky top-0 flex items-center h-20 px-6 bg-gradient-to-r from-[#8B0000] via-[#A52A2A] to-[#8B0000] dark:from-[#450a0a] dark:via-[#7f1d1d] dark:to-[#450a0a] shadow-lg border-b border-[#ffd700]/30 overflow-visible z-[70]">
      
      {/* --- Embedded Festive Styles --- */}
      <style>{`
        @keyframes float-clouds {
          0% { transform: translateX(0px); opacity: 0.3; }
          50% { transform: translateX(20px); opacity: 0.6; }
          100% { transform: translateX(0px); opacity: 0.3; }
        }
        @keyframes lantern-swing {
          0% { transform: rotate(-5deg); }
          100% { transform: rotate(5deg); }
        }
        .text-gold-gradient {
          background: linear-gradient(to bottom, #FFD700, #FDB931, #C06C84, #FFD700);
          -webkit-background-clip: text;
          color: transparent;
          background-clip: text;
        }
      `}</style>

      {/* --- Decorative Layer (Lower Z-Index) --- */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('https://www.transparenttextures.com/patterns/oriental-tiles.png')]"></div>
        
        {/* Lanterns - Moved inward slightly to avoid overlap */}
        <div className="absolute top-0 left-20 origin-top animate-[lantern-swing_3s_infinite_alternate_ease-in-out]">
            <Lantern size="lg" delay={0} />
        </div>
        <div className="absolute top-[-10px] left-48 origin-top scale-75 animate-[lantern-swing_4s_infinite_alternate_ease-in-out]" style={{animationDelay: '1s'}}>
            <Lantern size="md" />
        </div>
        <div className="absolute top-0 right-28 origin-top animate-[lantern-swing_3.5s_infinite_alternate_ease-in-out]" style={{animationDelay: '0.5s'}}>
            <Lantern size="lg" />
        </div>

        <div className="absolute -bottom-10 right-1/4 w-64 h-64 opacity-5 rotate-12">
            <GoldenHorse />
        </div>
        
        <Cloud className="absolute top-4 left-1/4 text-yellow-200/20 w-16 h-16 animate-[float-clouds_10s_infinite_ease-in-out]" />
        <Cloud className="absolute bottom-2 right-1/3 text-yellow-200/10 w-24 h-24 animate-[float-clouds_15s_infinite_ease-in-out_reverse]" />
      </div>

      {/* --- LEFT SECTION --- */}
      <div className="flex-1 flex items-center justify-start z-30">
        <button 
          onClick={onMenuToggle}
          className="lg:hidden p-2 text-yellow-100 hover:bg-red-900/30 rounded-lg transition-colors mr-4"
        >
          <Menu size={24} />
        </button>
      </div>

      {/* --- CENTER SECTION (Title) --- */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 flex flex-col items-center pointer-events-none md:pointer-events-auto">
          <h1 className="text-3xl font-display font-bold text-gold-gradient drop-shadow-md flex items-center gap-2 whitespace-nowrap">
          {title || "Dashboard"}

          </h1>
          <div className="h-[2px] w-32 bg-gradient-to-r from-transparent via-[#ffd700] to-transparent opacity-70 mt-1"></div>
      </div>

      {/* --- RIGHT SECTION --- */}
      <div className="flex-1 flex items-center justify-end gap-3 z-30">
        <CountdownTimer />

        <div className="flex items-center gap-2 ml-2">
            <Button 
                variant="ghost" 
                size="sm" 
                className="rounded-full w-10 h-10 p-0 hover:bg-red-900/50 text-yellow-100 hover:text-yellow-400 border border-transparent hover:border-yellow-500/30 transition-all"
                onClick={toggleTheme}
            >
            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </Button>

            <div className="relative" ref={dropdownRef}>
                <Button 
                    variant="ghost" 
                    size="sm" 
                    className={`rounded-full w-10 h-10 p-0 hover:bg-red-900/50 text-yellow-100 hover:text-yellow-400 border border-transparent hover:border-yellow-500/30 transition-all ${isOpen ? 'bg-red-900/50' : ''}`}
                    onClick={() => setIsOpen(!isOpen)}
                >
                <div className="relative">
                    <Bell size={20} />
                    {unreadCount > 0 && (
                        <span className="absolute -top-1.5 -right-1 w-4 h-4 bg-red-600 text-[10px] text-white flex items-center justify-center rounded-full border border-red-400 shadow-sm animate-bounce">
                            {unreadCount}
                        </span>
                    )}
                </div>
                </Button>

                {isOpen && (
                    <div className="absolute z-[80] right-0 mt-2 w-80 md:w-96 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-slate-700 overflow-hidden animate-fade-in-up origin-top-right">
                        <div className="p-3 border-b border-gray-100 dark:border-slate-800 flex justify-between items-center bg-gray-50/50 dark:bg-slate-800/50">
                            <h3 className="font-semibold text-gray-700 dark:text-gray-200 text-sm">Notifications</h3>
                            {unreadCount > 0 && (
                                <button onClick={markAllRead} className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1">
                                    <Check size={12} /> Mark all read
                                </button>
                            )}
                        </div>
                        
                        <div className="max-h-[400px] overflow-y-auto">
                            {notifications.length === 0 ? (
                                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                                    <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-20" />
                                    <p className="text-sm">No notifications yet</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-50 dark:divide-slate-800 cursor-default">
                                    {notifications.map(notif => (
                                        <div 
                                            key={notif.id} 
                                            onClick={() => !notif.is_read && markAsRead(notif.id)}
                                            className={`p-4 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors ${!notif.is_read ? 'bg-blue-50/30 dark:bg-blue-900/10' : ''}`}
                                        >
                                            <div className="flex gap-3">
                                                <div className="mt-1">
                                                    <div className={`w-2 h-2 rounded-full ${!notif.is_read ? 'bg-primary-500' : 'bg-transparent'}`}></div>
                                                </div>
                                                <div className="flex-1">
                                                    <h4 className={`text-sm ${!notif.is_read ? 'font-semibold text-gray-800 dark:text-gray-100' : 'text-gray-100 dark:text-gray-400'}`}>
                                                        {notif.title}
                                                    </h4>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                                                        {notif.message}
                                                    </p>
                                                    <span className="text-[10px] text-gray-400 mt-2 block">
                                                        {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true, locale: vi })}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <ProfileDropdown user={currentUser} />
        </div>
      </div>
    </header>
  );
};

export default Header;
